import { Button as MuiButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { IconChevronLeft } from "@tabler/icons-react";

// Base styled button
const BaseButton = styled(MuiButton)(() => ({
  textTransform: "none",
  borderRadius: "8px",
  padding: "6px 16px",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "24px",
  height: "40px",
  boxShadow: "0px 2px 0px 0px #0000000B",
  "&:hover": {
    boxShadow: "none",
  },
  "&:disabled": {
    backgroundColor: "#F5F5F5",
    borderColor: "#D9D9D9",
    color: "#BFBFBF",
    boxShadow: "none",
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "#F5F5F5",
      borderColor: "#D9D9D9",
      color: "#BFBFBF",
      boxShadow: "none",
    },
  },
  // minWidth: "100% !important",
}));

// Primary Button
const PrimaryButton = styled(BaseButton)({
  backgroundColor: "#1890FF",
  color: "white",
  border: "1px solid #1890FF",
  width: "100%",
  "&:hover": {
    backgroundColor: "#40A9FF",
    border: "1px solid #40A9FF",
  },
  "&:disabled": {
    backgroundColor: "#F5F5F5",
    borderColor: "#D9D9D9",
    color: "#BFBFBF",
    boxShadow: "none",
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "#F5F5F5",
      borderColor: "#D9D9D9",
      color: "#BFBFBF",
      boxShadow: "none",
    },
  },
});

// Secondary Button
const SecondaryButton = styled(BaseButton)({
  backgroundColor: "white",
  color: "black",
  border: "1px solid #D4D4D4",
  width: "100%",
  "&:hover": {
    border: "1px solid #D4D4D4",
  },
  "&:disabled": {
    backgroundColor: "#F5F5F5",
    borderColor: "#D9D9D9",
    color: "#BFBFBF",
    boxShadow: "none",
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "#F5F5F5",
      borderColor: "#D9D9D9",
      color: "#BFBFBF",
      boxShadow: "none",
    },
  },
});

// Tertiary Button
const TertiaryButton = styled(BaseButton)({
  backgroundColor: "transparent",
  color: "#1890FF",
  border: "1px solid transparent",
  boxShadow: "none !important",
  padding: "6px 8px",
  width: "fit-content",
  "&:hover": {
    backgroundColor: "rgba(24, 144, 255, 0.04)",
    border: "1px solid transparent",
  },
});

// Text Button
const TextButton = styled(BaseButton)({
  backgroundColor: "transparent",
  color: "black",
  border: "1px solid transparent",
  boxShadow: "none !important",
  padding: "6px 8px",
  width: "fit-content",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    border: "1px solid transparent",
  },
});

// Custom Color Button
const CustomButton = styled(BaseButton)({
  backgroundColor: "transparent",
  color: "inherit",
  border: "1px solid transparent",
  width: "100%",
  boxShadow: "none !important",
  "&:hover": {
    opacity: 0.9,
  },
});

// Link Button (no padding/margin, pure link style)
const LinkButton = styled(BaseButton)({
  backgroundColor: "transparent",
  color: "#1890FF",
  border: "none",
  boxShadow: "none !important",
  padding: "0",
  margin: "0",
  minHeight: "auto",
  height: "auto",
  width: "fit-content",
  fontSize: "inherit",
  fontWeight: "inherit",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
  "& .MuiButtonBase-root": {
    padding: "0",
    margin: "0",
  },
});

// Button Link (button style with link appearance)
const ButtonLinkButton = styled(BaseButton)({
  backgroundColor: "transparent",
  color: "#1890FF",
  border: "1px solid #1890FF",
  width: "100%",
  "&:hover": {
    backgroundColor: "rgba(24, 144, 255, 0.04)",
    border: "1px solid #40A9FF",
    textDecoration: "underline",
  },
  "&:disabled": {
    backgroundColor: "#F5F5F5",
    borderColor: "#D9D9D9",
    color: "#BFBFBF",
    boxShadow: "none",
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "#F5F5F5",
      borderColor: "#D9D9D9",
      color: "#BFBFBF",
      boxShadow: "none",
      textDecoration: "none",
    },
  },
});

// Back Button
const BackButton = styled(BaseButton)({
  backgroundColor: "transparent",
  color: "#1890FF",
  border: "1px solid transparent",
  boxShadow: "none !important",
  padding: "6px 8px",
  width: "fit-content",
  "&:hover": {
    backgroundColor: "rgba(24, 144, 255, 0.04)",
  },
  "& .MuiSvgIcon-root": {
    marginRight: "4px",
    fontSize: "18px",
  },
});

const Button = ({
  variant = "primary",
  children,
  startIcon,
  endIcon,
  ...props
}) => {
  if (variant === "back") {
    return (
      <BackButton startIcon={<IconChevronLeft size={16} />} {...props}>
        {children ?? "Back"}
      </BackButton>
    );
  }

  const VariantMap = {
    primary: PrimaryButton,
    secondary: SecondaryButton,
    tertiary: TertiaryButton,
    text: TextButton,
    link: LinkButton,
    buttonLink: ButtonLinkButton,
    custom: CustomButton,
  };

  const ButtonVariant = VariantMap[variant] || PrimaryButton;

  return (
    <ButtonVariant startIcon={startIcon} endIcon={endIcon} {...props}>
      {children}
    </ButtonVariant>
  );
};

export default Button;
